import { DOCUMENTO_REENVIADO, DOCUMENTO_REJEITADO } from '../../constants/requerimento-online-opcoes';
import { InstitutoPrevidenciarioDocumento, InstitutoPrevidenciarioHistorico } from '../../models/instituto-previdenciario.model';
import {
  RequerimentoOnlineDetalhe,
  RequerimentoOnlineSolicitacao,
} from '../../models/requerimento-beneficio/requerimento-online-solicitacao.model';

const PLANO = { 1: ['47', 'Plano CESP CD'], 2: ['48', 'Plano CD CPFL'], 3: ['51', 'ISA ENERGIA BRASIL PREV'], 4: ['46', 'Plano CD 2 Eletropaulo'] } as const;
const BENEFICIO: Record<string, number> = { Aposentadoria: 1, 'Aposentadoria antecipada': 2, 'Aposentadoria por invalidez': 3 };
const STATUS: Record<string, string> = { Solicitado: 'A', 'Documento rejeitado': 'P', 'Documento reenviado': 'E', 'Liberado para integração': 'L', Concluído: 'O', Cancelado: 'C' };

/** Monta o item com os códigos a partir dos rótulos, para a lista abaixo ficar legível. */
function item(
  idSolicitacao: number, nomeParticipante: string, matricula: number, plano: 1 | 2 | 3 | 4,
  tipoBeneficio: string, dataSolicitacao: string, status: string,
): RequerimentoOnlineSolicitacao {
  return {
    idSolicitacao, nomeParticipante, protocolo: String(202610100 + idSolicitacao), matricula,
    codPlano: PLANO[plano][0], plano: PLANO[plano][1], idBeneficio: BENEFICIO[tipoBeneficio], tipoBeneficio,
    dataSolicitacao, tpStatus: STATUS[status], status,
  };
}

/** Base do detalhe mockado ("Visualizar") até a PRE-346; a lista da tela vem da API. */
export const REQUERIMENTO_ONLINE_SOLICITACOES_MOCK: readonly RequerimentoOnlineSolicitacao[] = [
  item(1, 'Ana Paula Ferreira', 100231, 1, 'Aposentadoria', '2026-10-01', 'Solicitado'),
  item(2, 'Bruno Carvalho Lima', 100487, 2, 'Aposentadoria antecipada', '2026-10-01', 'Documento rejeitado'),
  item(3, 'Carla Mendes Souza', 100562, 3, 'Aposentadoria', '2026-10-06', 'Concluído'),
  item(4, 'Diego Ramos Oliveira', 100698, 4, 'Aposentadoria por invalidez', '2026-10-08', 'Cancelado'),
  item(5, 'Elisa Santos Rocha', 100733, 1, 'Aposentadoria', '2026-10-01', 'Liberado para integração'),
  item(6, 'Fábio Almeida Costa', 100815, 2, 'Aposentadoria antecipada', '2026-10-13', 'Solicitado'),
  item(7, 'Gabriela Nunes Pereira', 100902, 3, 'Aposentadoria', '2026-10-15', 'Liberado para integração'),
  item(8, 'Henrique Barbosa Dias', 101044, 4, 'Aposentadoria antecipada', '2026-10-17', 'Concluído'),
  item(9, 'Isabela Martins Teixeira', 101176, 1, 'Aposentadoria por invalidez', '2026-10-20', 'Solicitado'),
  item(10, 'João Victor Araújo', 101238, 2, 'Aposentadoria', '2026-10-21', 'Documento reenviado'),
  item(11, 'Karina Lopes Freitas', 101355, 3, 'Aposentadoria antecipada', '2026-10-22', 'Solicitado'),
  item(12, 'Lucas Moreira Cardoso', 101420, 4, 'Aposentadoria por invalidez', '2026-10-23', 'Cancelado'),
];

/** PDF fictício servido em todos os documentos enquanto não há API. */
export const REQUERIMENTO_DOCUMENTO_EXEMPLO_LINK = 'assets/mocks/requerimento-exemplo.pdf';

/** Documento específico de cada tipo de benefício, além da identificação e do comprovante de residência. */
const DOCUMENTO_POR_TIPO: Record<string, string> = {
  Aposentadoria: 'Carta de concessão do INSS',
  'Aposentadoria antecipada': 'Termo de opção pela aposentadoria antecipada',
  'Aposentadoria por invalidez': 'Laudo médico pericial',
};

/** Participantes que só precisam enviar o documento de identificação (um documento para validar). */
export const REQUERIMENTOS_COM_DOCUMENTO_UNICO: readonly number[] = [6];

/** Documento rejeitado na análise e, em "Documento reenviado", o que o participante mandou de novo. */
const TIPO_DOCUMENTO_REJEITADO = 'Comprovante de residência';

/** Motivo da rejeição do `TIPO_DOCUMENTO_REJEITADO`, também citado no histórico. */
const MOTIVO_REJEICAO = 'Documento ilegível: a imagem está cortada e sem foco. Envie uma nova cópia legível.';

/** Data e hora da etapa `indice` do histórico: a primeira às 09:15 do dia da solicitação, cada outra um dia depois, às 14:30. */
function dataHoraDaEtapa(item: RequerimentoOnlineSolicitacao, indice: number): string {
  const dia = new Date(`${item.dataSolicitacao ?? '2026-10-01'}T00:00:00`);
  dia.setDate(dia.getDate() + indice);
  const dois = (n: number) => String(n).padStart(2, '0');
  return `${dia.getFullYear()}-${dois(dia.getMonth() + 1)}-${dois(dia.getDate())}T${indice === 0 ? '09:15' : '14:30'}:00`;
}

/**
 * Documentos anexados pelo participante na solicitação. Todo requerimento tem documentos: todos
 * passam por "Solicitado", e os que seguiram adiante (inclusive os cancelados depois da liberação)
 * tiveram os documentos validados pelo administrador.
 */
function documentosMock(item: RequerimentoOnlineSolicitacao): InstitutoPrevidenciarioDocumento[] {
  const tipos = ['Documento de identificação (RG ou CNH)'];
  if (!REQUERIMENTOS_COM_DOCUMENTO_UNICO.includes(item.idSolicitacao)) {
    tipos.push(TIPO_DOCUMENTO_REJEITADO, DOCUMENTO_POR_TIPO[item.tipoBeneficio] ?? 'Formulário de requerimento');
  }

  return tipos.map((tipoDocumento, indice) => {
    const idDocumento = item.idSolicitacao * 10 + indice + 1;
    // Em "Documento rejeitado" ele fica rejeitado (2ª etapa); em "Documento reenviado", volta com
    // arquivo novo, na data do reenvio (3ª etapa). Nos dois, a avaliação guarda o motivo da rejeição.
    const rejeitado = item.status === 'Documento rejeitado' && tipoDocumento === TIPO_DOCUMENTO_REJEITADO;
    const reenviado = item.status === 'Documento reenviado' && tipoDocumento === TIPO_DOCUMENTO_REJEITADO;
    return {
      idDocumento,
      nomeArquivo: `${item.matricula}-documento-${indice + 1}${reenviado ? '-reenvio' : ''}.pdf`,
      tipoDocumento,
      contentType: 'application/pdf',
      enviadoEm: reenviado ? dataHoraDaEtapa(item, 2) : dataHoraDaEtapa(item, 0),
      link: REQUERIMENTO_DOCUMENTO_EXEMPLO_LINK,
      statusAvaliacao: rejeitado ? DOCUMENTO_REJEITADO : reenviado ? DOCUMENTO_REENVIADO : '',
      avaliacoes:
        rejeitado || reenviado
          ? [
              {
                idAvaliacao: idDocumento * 10 + 1,
                idDocumento,
                resultado: DOCUMENTO_REJEITADO,
                motivo: MOTIVO_REJEICAO,
                avaliadoPor: 'Administrador',
                avaliadoEm: dataHoraDaEtapa(item, 1),
                idOperador: 1,
              },
            ]
          : [],
    };
  });
}

/** Etapa do histórico: o status, quem fez a mudança e a informação daquele status. */
interface EtapaRequerimento {
  status: string;
  por: string;
  observacao: string | null;
}

const ETAPA_SOLICITADO: EtapaRequerimento = {
  status: 'Solicitado',
  por: 'Participante',
  observacao: 'Requerimento registrado pelo participante no Requerimento Online.',
};

const ETAPA_REJEITADO: EtapaRequerimento = {
  status: 'Documento rejeitado',
  por: 'Administrador',
  observacao: `Documento rejeitado: ${TIPO_DOCUMENTO_REJEITADO}. Motivo: ${MOTIVO_REJEICAO}`,
};

const ETAPA_LIBERADO: EtapaRequerimento = {
  status: 'Liberado para integração',
  por: 'Administrador',
  observacao: 'Documentos validados. Requerimento liberado para integração com o sistema de benefícios.',
};

/** Caminho até cada status atual, na ordem em que o participante passou pelas etapas. */
const ETAPAS_POR_STATUS: Record<string, readonly EtapaRequerimento[]> = {
  Solicitado: [ETAPA_SOLICITADO],
  'Documento rejeitado': [ETAPA_SOLICITADO, ETAPA_REJEITADO],
  'Documento reenviado': [
    ETAPA_SOLICITADO,
    ETAPA_REJEITADO,
    {
      status: 'Documento reenviado',
      por: 'Participante',
      observacao: `Documento reenviado para análise: ${TIPO_DOCUMENTO_REJEITADO}.`,
    },
  ],
  'Liberado para integração': [ETAPA_SOLICITADO, ETAPA_LIBERADO],
  Concluído: [
    ETAPA_SOLICITADO,
    ETAPA_LIBERADO,
    { status: 'Concluído', por: 'Sistema', observacao: 'Integração concluída. Benefício concedido ao participante.' },
  ],
  // Cancelado depois de os documentos serem validados (liberado para integração).
  Cancelado: [
    ETAPA_SOLICITADO,
    ETAPA_LIBERADO,
    { status: 'Cancelado', por: 'Participante', observacao: 'Cancelado a pedido do participante.' },
  ],
};

/**
 * Detalhe do item de mesmo `idSolicitacao` em `REQUERIMENTO_ONLINE_SOLICITACOES_MOCK`, para a
 * lista e o detalhe mostrarem o mesmo participante. O histórico tem todas as etapas pelas quais o
 * participante passou até o status atual (`ETAPAS_POR_STATUS`), cada uma com quem fez a mudança e a
 * informação do status. `undefined` quando o id não está na lista.
 */
export function requerimentoOnlineDetalheMock(idSolicitacao: number): RequerimentoOnlineDetalhe | undefined {
  const item = REQUERIMENTO_ONLINE_SOLICITACOES_MOCK.find((s) => s.idSolicitacao === idSolicitacao);
  if (!item) {
    return undefined;
  }

  // Mesmo número do protocolo de solicitação, na faixa 9xxxxxxxx dos cancelamentos.
  const protocoloCancelamento = item.status === 'Cancelado' ? Number(item.protocolo) + 700000000 : null;
  const etapas = ETAPAS_POR_STATUS[item.status] ?? [ETAPA_SOLICITADO, { status: item.status, por: 'Administrador', observacao: null }];

  // Uma mudança de status por etapa, um dia depois da anterior.
  const historico: InstitutoPrevidenciarioHistorico[] = etapas.map((etapa, i) => ({
    idHistorico: idSolicitacao * 10 + i + 1,
    statusAnterior: i === 0 ? null : etapas[i - 1].status,
    statusNovo: etapa.status,
    observacao:
      etapa.status === 'Cancelado' && etapa.observacao
        ? `${etapa.observacao} Protocolo de cancelamento: ${protocoloCancelamento}.`
        : etapa.observacao,
    alteradoPor: etapa.por,
    alteradoEm: dataHoraDaEtapa(item, i),
  }));

  return {
    ...item,
    protocoloCancelamento,
    ultimaAtualizacao: historico[historico.length - 1].alteradoEm?.slice(0, 10) ?? null,
    historico,
    docs: documentosMock(item),
  };
}
